import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';

import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { clearAll, setErrorMessage } from "../../../config/redux/slice/authSlice";
import { clearAll as clearAllApp } from "../../../config/redux/slice/appSlice";
import { clearAll as clearAllTransferee } from "../../../config/redux/slice/transfereeUserSlice";
import { clearAll as clearAllBank } from "../../../config/redux/slice/bankUserSlice";
import { clearAll as clearAllNotap } from "../../../config/redux/slice/notapUserSlice";

import { appUrls } from '../../../utils/urls/appUrls';
import { AuthAlert, Spinner } from '../../components';
import { ROLES } from '../../../constants';
import { loginUser } from '../../../config/redux/actions/auth';
import { setAuthTokenHeader } from '../../../services/http';

import AuthFormWrapper from '../../layout/AuthFormWrapper';

const authSchema = z.object({
  email: z.string({ required_error: '*this field is required' }).email(),
  password: z.string({ required_error: '*this field is required' }).min(3, 'Password length must be at least 3 characters')
});

function Login() {
    const [showPassword, setShowPassword] = useState(false);
    
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { register, handleSubmit, formState: {errors} } = useForm({ resolver: zodResolver(authSchema), mode: 'all' });
    const { loading, errorMsg } = useSelector((state) => state.authData)

    const clearResMsg = useCallback(() => {
        dispatch(clearAll({}))
        dispatch(clearAllTransferee({}))
        dispatch(clearAllApp({}))
        dispatch(clearAllBank({}))
        dispatch(clearAllNotap({}))
    }, [dispatch]);

    const getNextRouteFromResponse = useCallback((authData) => {
      if (!authData.isProfileComplete) {
        return appUrls.profileCreation;
      }

      if (authData.isAdmin) {
        return appUrls.adminHome;
      }

      if (authData.userRole === ROLES.BANK.TEXT) {
        return appUrls.bank_certificates;
      }

      if (authData.userRole === ROLES.TRANSFEREE.TEXT) {
        return appUrls.certificates;
      }
      
      return appUrls.notap_certificates;
    }, []);

    const closeAlert = useCallback(() => {
      dispatch(setErrorMessage({message: null}))
    }, [dispatch]);

    const onSubmit = async (data) => {
        const result = await dispatch(loginUser(data));

        if (loginUser.fulfilled.match(result)) {
          const nextRoute = getNextRouteFromResponse(result.payload.data);
          
          setAuthTokenHeader(result.payload.data.token);
          navigate(nextRoute);
        }
    };

    useEffect(()=>{
      clearResMsg();
    }, [clearResMsg]);

    return (
        <AuthFormWrapper sub_text='Enter your login credentials' onHandleSubmit={handleSubmit(onSubmit)}>
          {errorMsg && <AuthAlert onClick={closeAlert} alertStyles={{width: '95%', margin: '0 auto'}} status='error'>{errorMsg}</AuthAlert>}

          <div className="form-group col-md-12 mb-4">
            <label htmlFor='email'>Email</label>
            
            <input 
                type="email" 
                className="form-control input-lg" 
                id="email" 
                aria-describedby="emailHelp"
                {...register("email")}
            />

            {errors.email && <div className="text-danger small mt-1">{errors.email.message}</div>}
          </div>

          <div className="form-group col-md-12 ">
            <label htmlFor="password">Password</label>
            
            <div className="input-group mb-3">
                <input 
                    type={showPassword ? 'text' :'password'}
                    id="password"
                    className="form-control border-right-0"
                    {...register("password")}
                />

                <div className="input-group-append">
                    <span 
                      onClick={() => setShowPassword((previous) => !previous)} 
                      className={classNames({
                        "input-group-text bg-transparent mdi": true,
                        "mdi-eye-off-outline": showPassword,
                        "mdi-eye-outline": !showPassword,
                      })}
                    >
                    </span>
                </div>
            </div>

            {errors.password && <div className="text-danger small mt-1">{errors.password.message}</div>}
          </div>

          <div className="col-md-12">
            <div className="d-flex justify-content-end mb-3">
              <Link className="text-color" to={appUrls.forgotPassword}> Forgot password? </Link>
            </div>

            <div>
              {loading ? <Spinner/> : <button type="submit" className="btn btn-primary mb-4">Login</button>}
            </div>

            <p>
              Don't have an account yet?&nbsp;
              <Link className="text-blue" to={appUrls.register}>Register Here</Link>
            </p>
          </div>
        </AuthFormWrapper>
    );
}

export default Login;